<template>
  <div class="mt-4">
    <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
      Resumen de Orden
    </h4>

    <OrderSummaryCustomerInfo
      :order-type="orderType"
      :selected-customer="selectedCustomer"
      :selected-address="selectedAddress"
      :customer-name="customerName"
      :delivery-address="deliveryAddress"
      :order-notes="orderNotes"
    />

    <OrderSummaryDinersView
      :use-multiple-diners="useMultipleDiners"
      :persons-with-grouping="personsWithGrouping"
      :selected-items="selectedItems"
      :get-menu-item-name="getMenuItemName"
      :get-menu-item-category="getMenuItemCategory"
      :calculate-item-total="calculateItemTotal"
      :is-item-locked="isItemLocked"
      @decrease-quantity="$emit('decrease-quantity', $event)"
      @increase-quantity="$emit('increase-quantity', $event)"
    />

    <OrderSummaryTotals
      :order-type="orderType"
      :displayed-subtotal="displayedSubtotal"
      :displayed-final-total="displayedFinalTotal"
      :current-discount="currentDiscount"
      :final-total="finalTotal"
      :applied-promotions="appliedPromotions"
      :promotion-messages="promotionMessages"
      :is-calculating-promotions="isCalculatingPromotions"
      :delivery-fee="deliveryFee"
      :iva-enabled="ivaEnabled"
      :iva-rate="ivaRate"
      :iva-amount="ivaAmount"
      :show-create-button="showCreateButton"
      :is-create-disabled="isCreateDisabled"
      :button-text="buttonText"
      @create-order="$emit('create-order')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { OrderItem } from '@/services/orderService';
import type { GroupedOrderItem } from '@/utils/orderItemGrouping';
import { formatSpecialInstructions, groupOrderItems } from '@/utils/orderItemGrouping';
import OrderSummaryCustomerInfo from './OrderSummaryCustomerInfo.vue';
import OrderSummaryDinersView from './OrderSummaryDinersView.vue';
import OrderSummaryTotals from './OrderSummaryTotals.vue';
import type {
  DisplayOrderItem,
  Person,
  PersonWithGrouping,
  OrderCustomer,
  OrderAddress,
  AppliedPromotion
} from './orderSummaryTypes';

interface Props {
  useMultipleDiners: boolean;

  persons: Person[];
  selectedItems: DisplayOrderItem[];
  getMenuItemName: (itemId: number) => string;
  getMenuItemCategory?: (itemId: number) => string;
  calculateItemTotal: (item: DisplayOrderItem) => string;
  isItemLocked: (item: DisplayOrderItem) => boolean;
  showCreateButton?: boolean;
  isCreateDisabled?: boolean;
  buttonText?: string;
  ivaEnabled?: boolean;
  ivaRate?: number;
  ivaAmount?: number;
  subtotal?: number;
  // Customer and order info for summary
  orderType?: string;
  selectedCustomer?: OrderCustomer | null;
  selectedAddress?: OrderAddress | null;
  customerName?: string;
  deliveryAddress?: string;
  // Delivery fee
  deliveryFee?: number;
  // Promotions
  currentDiscount?: number;
  finalTotal?: number;
  appliedPromotions?: AppliedPromotion[];
  promotionMessages?: string[];
  isCalculatingPromotions?: boolean;
  // Order notes
  orderNotes?: string;
}

const props = defineProps<Props>();

defineEmits<{
  (e: 'remove-item', personIndex: number, itemIndex: number): void;
  (e: 'decrease-quantity', item: DisplayOrderItem): void;
  (e: 'increase-quantity', item: DisplayOrderItem): void;
  (e: 'create-order'): void;
}>();


// Helper function to convert person items to OrderItem format for grouping
function convertPersonItemsToOrderItems(personItems: Person['items']): OrderItem[] {
  return personItems.map((item, index) => ({
    id: index,
    menu_item_id: item.menu_item_id,
    variant_id: item.variant_id || null,
    quantity: item.quantity || 1,

    special_instructions: item.special_instructions || item.notes || null,
    status: 'pending',
    order_id: 0,
    person_id: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    unit_price: item.unit_price || 0,
    item_type: item.item_type || 'dine_in',
    variant: item.variant || null,
    menu_item: {
      id: item.menu_item_id,
      name: props.getMenuItemName(item.menu_item_id),

      description: '',
      price: item.unit_price || 0,
      category: props.getMenuItemCategory ? props.getMenuItemCategory(item.menu_item_id) : '',
      category_visible_in_kitchen: true,
      image_url: '',
      is_available: true
    },
    extras: []
  }));
}

// Computed property for subtotal calculation
const calculatedSubtotal = computed(() => {
  if (props.useMultipleDiners) {
    return props.persons.reduce((sum, person) => 
      sum + person.items.reduce((itemSum, item) => 
        itemSum + ((item.unit_price || 0) * item.quantity), 0), 0);
  } else {
    return props.selectedItems.reduce((sum, item) => 
      sum + (parseFloat(props.calculateItemTotal(item)) || 0), 0);
  }
});

const displayedSubtotal = computed(() => {
  return props.subtotal ?? calculatedSubtotal.value;
});

const fallbackFinalTotal = computed(() => {
  return displayedSubtotal.value + (props.ivaAmount || 0) + (props.deliveryFee || 0);
});

const displayedFinalTotal = computed(() => {
  return props.finalTotal ?? fallbackFinalTotal.value;
});

// Create grouped items for each person
const personsWithGrouping = computed(() => {
  return props.persons.map(person => {
    const orderItems = convertPersonItemsToOrderItems(person.items);
    const groupedItems = groupOrderItems(orderItems);

    return {
      ...person,
      groupedItems,
      totalItemCount: orderItems.length,
      groupCount: groupedItems.length
    };
  });
});
</script>
